import { createRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { completeGoogleSignIn } from "../../api/generated/endpoints/identity/identity";
import { forgetMediaAccess } from "../../platform/media/access";
import { rootRoute } from "../../platform/routes";
import { redirectIfSignedIn, wayBack } from "../../platform/session";
import { browserTimeZone } from "../../platform/time-zone";
import { AcceptInvitationScreen } from "./AcceptInvitation";
import { GoogleCallbackError, GoogleCancelled, GoogleFinishing } from "./GoogleCallback";
import { SignInScreen } from "./SignInScreen";

// A malformed `redirect` (not a string) is dropped, never an error page.
const signInSearch = z.object({ redirect: z.string().optional().catch(undefined) });

export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in",
  staticData: { public: true },
  validateSearch: (search) => signInSearch.parse(search),
  beforeLoad: ({ context, search }) => redirectIfSignedIn(context.queryClient, search.redirect),
  component: SignInScreen,
});

// What Google appends to the redirect URI: `code` and `state`, or `error` when the user cancels.
const googleSearch = z.object({
  code: z.string().optional().catch(undefined),
  state: z.string().optional().catch(undefined),
  error: z.string().optional().catch(undefined),
});

/**
 * Google's redirect URI (A1 decision 2). The loader posts the code once per navigation, so a
 * re-render can never post a spent code again, and the error screen offers no retry.
 */
export const googleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "sign-in/google",
  staticData: { public: true },
  validateSearch: (search) => googleSearch.parse(search),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps: { code, state, error } }) => {
    if (error !== undefined || code === undefined || state === undefined) return;
    const signedIn = await completeGoogleSignIn({ code, state, time_zone: browserTimeZone() });
    context.queryClient.clear();
    forgetMediaAccess();
    throw redirect({ href: wayBack(signedIn.redirect ?? undefined), replace: true });
  },
  pendingMs: 0,
  pendingComponent: GoogleFinishing,
  component: GoogleCancelled,
  errorComponent: GoogleCallbackError,
});

/**
 * The link in the invitation email, `/invitations/accept#token=...`. Under the root, not the
 * session guard: the guard would put the URL into `?redirect=`, and the page handles a missing
 * session itself.
 */
export const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "invitations/accept",
  staticData: { public: true },
  component: AcceptInvitationScreen,
});
